import { Constants } from "src/billinglatest/constants";
import type { PixiAddress } from "../../../hooks/usePixiWizardState";
import { isShippingSupported } from "./supportedShippingCountries";

/** Fields on {@link PixiAddress} that can carry a validation error. */
export type AddressField = keyof PixiAddress;

export type AddressErrors = Partial<Record<AddressField, boolean>>;

/**
 * Email format check. Intentionally permissive — matches `something@domain.tld`
 * and rejects obvious non-emails like `sdfs` (the Figma error example).
 */
export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** Whether the given country uses a state/province dropdown (US, CA). */
export const countryHasState = (countryCode: string): boolean =>
  Object.keys(Constants.States).includes(countryCode);

interface ValidateAddressOptions {
  /** Billing collects a state field for state-countries; shipping does not. */
  requireState: boolean;
  /** Shipping requires a country we actually ship hardware to. */
  requireShippingSupported: boolean;
}

/**
 * Compute per-field validation errors for an address. Required fields are
 * first name, email, country, street, city and postal code (company, second
 * name, phone and building are optional). Email must additionally be a valid
 * format. Billing additionally requires a state when the country has states;
 * shipping additionally requires a supported shipping country.
 */
export const validateAddress = (
  address: PixiAddress,
  options: ValidateAddressOptions,
): AddressErrors => {
  const errors: AddressErrors = {};

  if (!address.firstName.trim()) errors.firstName = true;
  if (!address.email.trim() || !isValidEmail(address.email)) {
    errors.email = true;
  }
  if (!address.country.trim()) errors.country = true;
  if (!address.street.trim()) errors.street = true;
  if (!address.city.trim()) errors.city = true;
  if (!address.postalCode.trim()) errors.postalCode = true;

  if (
    options.requireState &&
    countryHasState(address.country) &&
    !address.state.trim()
  ) {
    errors.state = true;
  }

  if (
    options.requireShippingSupported &&
    address.country.trim() &&
    !isShippingSupported(address.country)
  ) {
    errors.country = true;
  }

  return errors;
};

export const hasErrors = (errors: AddressErrors): boolean =>
  Object.values(errors).some(Boolean);
